import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { INavbarActionItem } from '../../models/irembogov-navbar-action-item.model';
import { INavbarItem } from '../../models/irembogov-navbar-item.model';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { KeycloakService } from 'keycloak-angular';
import { IGetProfileResponse } from '../../interfaces';
import IIremboUserProfile from '../../../auth/models/profile/user-profile-model';
import { AdminService } from '../../services/admin/admin.service';
import { AnalyticsRoleUtils } from '../../services/analytics/analytics-role.utils';

@Component({
  standalone: false,
  selector: 'irembogov-navbar',
  templateUrl: './irembogov-navbar.component.html',
  styleUrls: ['./irembogov-navbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class IrembogovNavbarComponent implements OnChanges {
  @Input() navbarItems: INavbarItem[] = [];
  @Input() notifications?: any[] = [];
  @Input() navbarActions: INavbarActionItem[] = [];
  @Input() navbarWide = true;
  @Input() showNotification = true;
  @Input() showActiveRole = false;
  @Input() activeRole = '';
  @Input() selectedOrganization: any;
  @Output() actionClick: EventEmitter<any> = new EventEmitter();

  isLoggedIn: boolean | unknown;
  userProfile: IIremboUserProfile = {} as IIremboUserProfile;

  isMenuVisible = false;
  columns: INavbarItem[][] = [];

  constructor(
    private router: Router,
    private analyticsService: AnalyticsService,
    private keycloakService: KeycloakService,
    private adminService: AdminService
  ) {
    this.initializeAsync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['navbarItems'] &&
      changes['navbarItems'].currentValue &&
      changes['navbarItems'].currentValue?.length !==
        changes['navbarItems'].previousValue?.length
    ) {
      this.distributeItems(3);
    }
  }

  private async initializeAsync(): Promise<void> {
    this.isLoggedIn = await this.keycloakService.isLoggedIn();
    if (this.isLoggedIn) {
      await this.getUserProfile();
    }
  }

  getQueryParams(queryParams: string | Record<string, unknown> | undefined) {
    if (queryParams === undefined || typeof queryParams === 'string') {
      return null;
    }
    return queryParams;
  }

  getNavItemExactRouteCheck(item: INavbarItem): boolean {
    if (item.exactRouteAsActive) {
      return item.exactRouteAsActive;
    }

    return true;
  }

  async getUserProfile() {
    const userProfileId = (await this.keycloakService.loadUserProfile())
      .id as string;

    this.adminService.getUserById(userProfileId).subscribe({
      next: (payload: IGetProfileResponse) => {
        this.userProfile = payload.data;
      },
      error: err => {
        console.error('Error:', err);
      },
    });
  }

  navigateTo(item: INavbarItem) {
    const locale = localStorage.getItem('locale') ?? 'en-US';
    /* Track report navigation event to mixpanel */
    if (item?.mixPanelEventOnClick !== undefined) {
      this.analyticsService.trackEvent({
        eventType: item?.mixPanelEventOnClick,
        institution_id: this.selectedOrganization?.['id'],
        language: locale,
        user_role: AnalyticsRoleUtils.getUserRole([
          item.activeRole ?? this.keycloakService.getUserRoles()[0],
        ]),
      });
    }

    const navigationExtras: NavigationExtras = {
      queryParams: this.getQueryParams(item.queryParams),
    };
    this.router.navigate([item.routerLink], navigationExtras);
    this.hideMenu();
  }

  isLinkActive(item: any): boolean {
    return this.router.isActive(item.routerLink, {
      paths: this.getNavItemExactRouteCheck(item) ? 'exact' : 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  distributeItems(numberOfColumns: number): void {
    this.columns = [];
    const itemsPerColumn = Math.ceil(this.navbarItems.length / numberOfColumns);

    for (let i = 0; i < numberOfColumns; i++) {
      const start = i * itemsPerColumn;
      const end = start + itemsPerColumn;

      this.columns.push(this.navbarItems.slice(start, end));
    }
  }

  showMenu() {
    this.isMenuVisible = true;
  }

  hideMenu() {
    this.isMenuVisible = false;
  }
}
